import type React from "react";
import { forwardRef } from "react";
import { Box } from "@mui/material";

export interface GlassCardProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: "default" | "strong" | "subtle";
  hover?: boolean;
}

const GlassCard = forwardRef<HTMLDivElement, GlassCardProps>(
  ({ className, variant = "default", hover = true, style, ...props }, ref) => {
    const baseStyle: React.CSSProperties = {
      backdropFilter: "blur(16px)",
      backgroundColor: "rgba(255, 255, 255, 0.05)",
      border: "1px solid rgba(255, 255, 255, 0.1)",
      borderRadius: "12px",
      boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
      transition: "all 0.3s ease",
      ...style,
    };

    const variantStyles: Record<string, React.CSSProperties> = {
      strong: {
        backdropFilter: "blur(24px)",
        backgroundColor: "rgba(255, 255, 255, 0.08)",
        border: "1px solid rgba(255, 255, 255, 0.15)",
        boxShadow: "0 12px 40px rgba(0, 0, 0, 0.4)",
      },
      subtle: {
        backdropFilter: "blur(8px)",
        backgroundColor: "rgba(255, 255, 255, 0.03)",
        border: "1px solid rgba(255, 255, 255, 0.05)",
      },
    };

    const hoverStyle = hover
      ? {
          "&:hover": {
            boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
            transform: "translateY(-4px)",
          },
        }
      : {};

    return (
      <Box
        ref={ref}
        className={className}
        sx={{
          ...baseStyle,
          ...variantStyles[variant],
          ...hoverStyle,
        }}
        {...props}
      />
    );
  }
);
GlassCard.displayName = "GlassCard";

export { GlassCard };

